'use client';

// imports
import Link from 'next/link';

// lib files
import { formatAmount } from '@/lib/helpers/format-amount';
import { PASSPORT_LOOKUP_URL } from '@/lib/constants';
import { StationData } from "@/lib/types/api/stations-data";
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';

// components
import PassportDonateButton from '@/components/PassportDonateButton/PassportDonateButton';
import PassportFeatureTable from '@/components/PassportLearnMore/PassportFeatureTable/PassportFeatureTable';

// svgs
import HeartIcon from '@/public/svg/donate-heart.svg';

// styles
import styles from './PassportLearnMoreHero.module.scss';

interface PassportLearnMoreHeroContentProps {
  depUserIsLocalizedPassport?: boolean;
  stationData: StationData;
}

/**
 * The client-side content of the Passport Learn More Hero
 * By breaking this into a client component, we can render the content
 * based on whether the user is logged in and their passport status.
 * This allows for smoother animating of the area so that it looks nice.
 * @param {PassportLearnMoreHeroContentProps} props
 * @returns {JSX.Element} The rendered content
*/
const PassportLearnMoreHeroContent = (props: PassportLearnMoreHeroContentProps) => {
  const { depUserIsLocalizedPassport, stationData } = props;
  const { attributes } = stationData;
  const { short_common_name, annual_passport_qualifying_amount } = attributes;
  const { cachedIdentity, isResolving } = useIdentity();
  const hasMounted = useHasMounted();

  // Default values for non-Passport members or users not localized to the membership station
  const donationText = "Your donation goes to your local station!";
  let ctaText = <>Support your local station and enjoy extended <br />access to your favorite PBS shows & films</>;
  let memberLookup: React.ReactNode | null = (
    <p className={styles.member_lookup}>
      Already a Member?
      <a href={PASSPORT_LOOKUP_URL} target="_blank" rel="noopener noreferrer">
        Check Your Passport Status
      </a>
    </p>
  )
  let ctaDescription: React.ReactNode | null = <>Your donation will help {short_common_name} serve your community through media that educates, inspires, and entertains.</>;

  if (annual_passport_qualifying_amount !== null) {
    const annualAmount = formatAmount(annual_passport_qualifying_amount);
    const monthlyAmount = formatAmount(annual_passport_qualifying_amount / 12);
    ctaText = <>Donate at least ${monthlyAmount} per month or ${annualAmount} per year and enjoy {short_common_name} Passport</>
  }

  // Cached rather than confirmed: this only chooses between member and
  // non-member donation copy, so rendering it from a localStorage profile a
  // moment before /sso/profile confirms is better than flashing the wrong copy.
  const isLocalizedPassportMember =
    depUserIsLocalizedPassport ? depUserIsLocalizedPassport :
    (cachedIdentity?.isPassportMemberOf(stationData) ?? false);

  // Set values if user is a Passport member and localized to the membership station
  if (isLocalizedPassportMember) {
    ctaText = <>Thank you for being a member! You are supporting America’s largest classroom, the nation’s largest stage for the arts, and a trusted window into the world.</>;
    memberLookup = null;
    ctaDescription = null;
  }

  // Gated on mount for the same reason: isResolving is false as soon as a cached
  // profile is held, which the client knows on its first render and the server
  // does not.
  const shouldRenderContent = depUserIsLocalizedPassport !== undefined || (hasMounted && !isResolving);

  if (shouldRenderContent) {
    return (
      <div className={styles.passport_learn_more_hero_content} data-testid="passport-learn-more-hero-content">
        <div className={styles.passport_learn_more_hero_donate_blurb}>
          {ctaText && <p className={styles.donate_text}>{ctaText}</p>}
          {ctaDescription && <p className={styles.donate_description}>{ctaDescription}</p>}
          <PassportDonateButton 
            stationData={stationData} 
            depUserIsLocalizedPassport={depUserIsLocalizedPassport} 
            hideHeartIcon={true} 
          />
          {memberLookup}
          {isLocalizedPassportMember && <p className={styles.donation_text_passport_member}><HeartIcon />{donationText}</p>}
          <PassportFeatureTable stationData={stationData} />
          {!isLocalizedPassportMember && <p className={styles.donation_text_non_passport_member}><HeartIcon />{donationText}</p>}
        </div>
      </div>
    );
  }

  return null;
};

export default PassportLearnMoreHeroContent;
